import { Avatar, Badge } from '@main/core-ui';
import {
  AccessiblePopover,
  FocusButton,
  usePopover,
} from '@main/pc-core-components';
import type { SombraCoreIdentifier, SombraProfile } from '@main/sombra-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { selectActiveDataSubject } from '../../containers/ActiveDataSubject/selectors';
import { footerMessages } from '../../containers/Footer/messages';
import { avatarPopoverMessages } from './messages';
import {
  AvatarIconWrapper,
  AvatarPopoverWrapper,
  ControlsWrapper,
  SelectionWrapper,
  StatusWrapper,
  UserWrapper,
} from './wrappers';

/**
 * The AvatarPopover props
 */
export interface AvatarPopoverProps extends SombraProfile {
  /** The mail-to email for contact support inquiries */
  supportEmail?: string;
  /** A function to logout the user */
  logout: () => void;
  /** Core identifier for logged in session */
  coreIdentifier?: SombraCoreIdentifier;
}

/**
 * Profile Avatar on Header
 */
function AvatarPopover({
  picture,
  nickname,
  logout,
  email,
  supportEmail,
  coreIdentifier,
}: AvatarPopoverProps): JSX.Element {
  const { formatMessage } = useIntl();
  const statusMessage = nickname
    ? avatarPopoverMessages.statusNickname
    : avatarPopoverMessages.statusId;
  const activeDataSubject = useSelector(selectActiveDataSubject);
  const popover = usePopover();
  const { openPopover } = popover;

  const content = (
    <AvatarPopoverWrapper id="profile-controls">
      <StatusWrapper role="none">
        <Badge color="success">{formatMessage(statusMessage)}</Badge>
        <UserWrapper>
          <h1>{nickname || email || coreIdentifier?.value}</h1>
          {activeDataSubject && <p>{formatMessage(activeDataSubject.title)}</p>}
        </UserWrapper>
      </StatusWrapper>
      <ControlsWrapper role="menu">
        {supportEmail && (
          <li role="menuitem">
            <SelectionWrapper
              onClick={() => {
                window.location.href = `mailto:${supportEmail}`;
              }}
            >
              {formatMessage(footerMessages.support)}
            </SelectionWrapper>
          </li>
        )}
        <li role="menuitem">
          <SelectionWrapper onClick={logout}>
            {formatMessage(avatarPopoverMessages.endSession)}
          </SelectionWrapper>
        </li>
      </ControlsWrapper>
    </AvatarPopoverWrapper>
  );

  return (
    <div id="profile-avatar-popover">
      <AccessiblePopover popover={popover} yPosition="bottom" xPosition="right">
        {content}
      </AccessiblePopover>
      <FocusButton
        aria-haspopup="true"
        aria-controls="profile-controls"
        aria-label={formatMessage(avatarPopoverMessages.profileMenuLabel)}
        onClick={openPopover}
      >
        <AvatarIconWrapper>
          <Avatar
            icon="user"
            src={picture}
            alt={
              nickname
                ? formatMessage(avatarPopoverMessages.avatarAltWithNickname, {
                    nickname,
                  })
                : formatMessage(avatarPopoverMessages.avatarAlt)
            }
            size={40}
          />
        </AvatarIconWrapper>
      </FocusButton>
    </div>
  );
}

export default AvatarPopover;
